import { datePl, type InvoiceReceiptProposalDto, type InvoiceWarehouseDto, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Checkbox, Group, Modal, NumberInput, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { useMe, can } from '../../auth';
import { Money, MoneyInput } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { SectionCard } from '../../components/ui';
import { qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { useWarehouses } from '../../data';
import { ItemSelect, useStockItems, WarehouseSelect } from '../warehouse/common';

const CONF: Record<string, { label: string; color: string }> = { pewne: { label: 'dopasowane', color: 'green' }, propozycja: { label: 'propozycja — sprawdź', color: 'yellow' }, brak: { label: 'nie dopasowano', color: 'gray' } };

/**
 * Faktura zakupu w magazynie (docs/14 §2.10): pobranie faktury nie zmienia stanu. Przyjęcie z faktury
 * (ilości potwierdza magazynier) albo powiązanie z wcześniejszym przyjęciem — bez ponownego zwiększenia stanu.
 */
export function PurchaseWarehouse({ invoiceId, companyId }: { invoiceId: string; companyId: string }) {
  const me = useMe().data;
  // moduł magazynu wyłączony — faktura zakupu bez sekcji „Magazyn” (jak przed modułem)
  const off = !!me && !me.features.magazyn;
  const q = useQuery({ queryKey: ['purchase-warehouse', invoiceId], queryFn: () => api<InvoiceWarehouseDto>(`/finance/purchases/${invoiceId}/warehouse`), enabled: !!me && !off });
  const [receiving, setReceiving] = useState(false);
  const [linking, setLinking] = useState(false);
  const qc = useQueryClient();
  const set = (r: InvoiceWarehouseDto) => qc.setQueryData(['purchase-warehouse', invoiceId], r);
  const correct = useMutation({
    mutationFn: (linkId: string) => api<InvoiceWarehouseDto>('/purchase-links/value-correction', { method: 'POST', body: { linkId, reason: 'Cena z faktury inna niż z przyjęcia' } }),
    onSuccess: (r) => (set(r), notifyOk('Skorygowano wartość magazynu')),
    onError: notifyError,
  });
  const corrective = q.data?.corrective ?? null;
  const canMove = !!me && can(me, 'magazyn.ruchy') && !!q.data && !corrective;
  const canLink = !!me && can(me, 'zakupy.zamowienia') && !!q.data && !corrective;
  if (off) return null;
  return (
    <SectionCard
      title="Magazyn"
      padding="sm"
      actions={
        <Group gap={4}>
          {canMove && (
            <Button size="xs" variant="light" onClick={() => setReceiving(true)}>
              Przyjmij do magazynu
            </Button>
          )}
          {canLink && (
            <Button size="xs" variant="subtle" onClick={() => setLinking(true)}>
              Powiąż z przyjęciem
            </Button>
          )}
        </Group>
      }
    >
      <QueryState isLoading={q.isLoading} error={q.error} lines={2}>
        {corrective ? (
          <Alert variant="light" color="blue" p="xs">
            <Text size="sm">
              Faktura korygująca{corrective.correctedNumber ? ` do ${corrective.correctedNumber}` : ''} — towaru z niej się nie przyjmuje. Skoryguj ilość lub wartość przyjęcia pierwotnej faktury
              {corrective.receipts.length === 0 && ' (faktura pierwotna nie ma przyjęcia w magazynie)'}.
            </Text>
            {corrective.receipts.length > 0 && (
              <Group gap={6} mt={4}>
                <Text size="xs">Przyjęcia faktury pierwotnej:</Text>
                {corrective.receipts.map((r) =>
                  me && can(me, 'magazyn.podglad') ? (
                    <Anchor key={r.documentId} component={Link} to={`/magazyn/dokumenty?dokument=${r.documentId}`} size="xs">
                      {r.number}
                    </Anchor>
                  ) : (
                    <Text key={r.documentId} size="xs">
                      {r.number}
                    </Text>
                  ),
                )}
              </Group>
            )}
          </Alert>
        ) : q.data && q.data.links.length === 0 ? (
          <Text size="sm" c="dimmed">
            Faktura nie jest powiązana z przyjęciem. Samo pobranie faktury z KSeF nie zmienia stanu magazynu.
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Lp.</Table.Th>
                <Table.Th>Przyjęcie</Table.Th>
                <Table.Th ta="right">Ilość</Table.Th>
                <Table.Th ta="right">Różnica ceny</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {q.data?.links.map((l) => (
                <Table.Tr key={l.id}>
                  <Table.Td>{l.lp}</Table.Td>
                  <Table.Td>
                    {me && can(me, 'magazyn.podglad') ? (
                      <Anchor component={Link} to={`/magazyn/dokumenty?dokument=${l.documentId}`} size="sm">
                        {l.documentNumber}
                      </Anchor>
                    ) : (
                      <Text size="sm">{l.documentNumber}</Text>
                    )}
                    <Text size="xs" c="dimmed">
                      {l.itemName}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{qtyPl(l.quantity)}</Table.Td>
                  <Table.Td ta="right">{l.differenceGr === null ? '—' : l.differenceGr === 0 ? 'zgodna' : <Money gr={l.differenceGr} />}</Table.Td>
                  <Table.Td>
                    {l.corrected && (
                      <Badge size="xs" variant="light" color="green">
                        skorygowano
                      </Badge>
                    )}
                    {!!l.differenceGr && !l.corrected && me && can(me, 'magazyn.korekty') && (
                      <Button size="compact-xs" variant="subtle" onClick={() => correct.mutate(l.id)} loading={correct.isPending}>
                        Koryguj wartość
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {!!q.data?.unlinkedLps.length && q.data.links.length > 0 && (
          <Text size="xs" c="dimmed" mt={4}>
            Pozycje bez przyjęcia (np. usługi, transport): {q.data.unlinkedLps.join(', ')}
          </Text>
        )}
      </QueryState>
      {receiving && <ReceiveModal invoiceId={invoiceId} companyId={companyId} onClose={() => setReceiving(false)} onDone={(r) => (set(r), setReceiving(false), notifyOk('Przyjęto do magazynu'))} />}
      {linking && <LinkModal invoiceId={invoiceId} onClose={() => setLinking(false)} onDone={(r) => (set(r), setLinking(false), notifyOk('Powiązano fakturę z przyjęciem'))} />}
    </SectionCard>
  );
}

type Row = { lp: number; include: boolean; itemId: string | null; packId: string | null; quantity: number; unitPriceGr: number; orderLineId: string | null };

function ReceiveModal({ invoiceId, companyId, onClose, onDone }: { invoiceId: string; companyId: string; onClose: () => void; onDone: (r: InvoiceWarehouseDto) => void }) {
  const q = useQuery({ queryKey: ['receipt-proposal', invoiceId], queryFn: () => api<InvoiceReceiptProposalDto>(`/finance/purchases/${invoiceId}/receipt-proposal`) });
  const items = useStockItems();
  const [rows, setRows] = useState<Row[]>([]);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const whs = useWarehouses();
  useEffect(() => {
    // jeden (albo domyślny) magazyn firmy z faktury — wybrany od razu
    const own = (whs.data ?? []).filter((w) => w.active && w.companyId === companyId);
    if (!warehouseId && own.length) setWarehouseId((own.find((w) => w.isDefault) ?? (own.length === 1 ? own[0] : null))?.id ?? null);
  }, [whs.data, companyId, warehouseId]);
  const [docDate, setDocDate] = useState(todayInWarsaw());
  // kurs waluty faktury (z KursWaluty) — do zmiany; PLN — bez pola
  const [rate, setRate] = useState<number | null>(null);
  const foreign = !!q.data && q.data.currency !== 'PLN';
  const key = useMemo(() => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now()}`), []);
  useEffect(() => {
    if (!q.data) return;
    setRate(q.data.currency === 'PLN' ? null : q.data.exchangeRate);
    setRows(
      q.data.lines.map((l) => ({
        lp: l.lp,
        include: !!l.match && l.linkedQuantity === 0,
        itemId: l.match?.itemId ?? null,
        packId: l.match?.packId ?? null,
        quantity: l.quantity ?? 1,
        // cena za jednostkę magazynową: cena pozycji faktury / przelicznik opakowania
        unitPriceGr: l.unitNetGr === null ? 0 : Math.round(l.unitNetGr / (l.match?.factor ?? 1)),
        orderLineId: l.orderLineId,
      })),
    );
  }, [q.data]);
  const set = (lp: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.lp === lp ? { ...r, ...patch } : r)));
  const m = useMutation({
    mutationFn: () =>
      api<InvoiceWarehouseDto>(`/finance/purchases/${invoiceId}/receive`, {
        method: 'POST',
        body: {
          warehouseId,
          supplierId: q.data?.supplier.id ?? null,
          docDate,
          exchangeRate: foreign ? rate : null,
          requestKey: key,
          lines: rows.filter((r) => r.include && r.itemId).map((r) => ({ lp: r.lp, itemId: r.itemId, packId: r.packId, quantity: r.quantity, unitPriceGr: r.unitPriceGr, orderLineId: r.orderLineId })),
        },
      }),
    onSuccess: onDone,
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Przyjęcie do magazynu z faktury" size="90rem">
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <Stack>
            <Text size="sm">
              Dostawca: <b>{q.data.supplier.name}</b> ({q.data.supplier.nip}){!q.data.supplier.matched && ' — zostanie dodany do dostawców'}.
              {q.data.openOrders.length > 0 && ` Otwarte zamówienia: ${q.data.openOrders.map((o) => o.number).join(', ')}.`}
            </Text>
            <Alert variant="light" color="yellow" p="xs">
              Wpisz ilości faktycznie otrzymane. Niepewne dopasowania (żółte) sprawdź przed zatwierdzeniem; pozycje niemagazynowe (usługi, transport) odznacz.
            </Alert>
            <Group grow>
              <WarehouseSelect label="Magazyn" value={warehouseId} onChange={setWarehouseId} companyId={companyId} />
              <TextInput label="Data przyjęcia" type="date" value={docDate} onChange={(e) => setDocDate(e.currentTarget.value)} />
              {foreign && (
                <NumberInput
                  label={`Kurs ${q.data.currency}/PLN`}
                  description={q.data.exchangeRate ? 'z faktury' : 'faktura bez kursu — wpisz'}
                  min={0.000001}
                  decimalScale={6}
                  decimalSeparator=","
                  value={rate ?? ''}
                  onChange={(v) => setRate(Number(v) > 0 ? Number(v) : null)}
                  error={!rate ? 'Podaj kurs większy od 0' : undefined}
                />
              )}
            </Group>
            {foreign && (
              <Text size="xs" c="dimmed">
                Ceny w {q.data.currency} — wartość magazynu w PLN po kursie.
              </Text>
            )}
            <Table.ScrollContainer minWidth={900}>
              <Table verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Pozycja faktury</Table.Th>
                    <Table.Th>Produkt w katalogu</Table.Th>
                    <Table.Th w={110}>Otrzymano</Table.Th>
                    <Table.Th w={130}>Cena / jedn. mag.{foreign ? ` (${q.data.currency})` : ''}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {q.data.lines.map((l) => {
                    const r = rows.find((x) => x.lp === l.lp);
                    if (!r) return null;
                    const it = items.data?.find((x) => x.id === r.itemId);
                    return (
                      <Table.Tr key={l.lp} style={{ opacity: r.include ? 1 : 0.5 }}>
                        <Table.Td>
                          <Checkbox checked={r.include} onChange={(e) => set(l.lp, { include: e.currentTarget.checked })} aria-label="Przyjmij pozycję" />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {l.lp}. {l.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {l.quantity} {l.unit}
                            {l.index ? ` · indeks ${l.index}` : ''}
                            {l.gtin ? ` · EAN ${l.gtin}` : ''}
                            {l.linkedQuantity ? ` · już przyjęto ${l.linkedQuantity}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <ItemSelect size="xs" items={items.data ?? []} value={r.itemId} onChange={(v) => set(l.lp, { itemId: v, packId: null })} placeholder="Wybierz produkt" />
                          <Group gap={4} mt={2}>
                            <Badge size="xs" color={CONF[l.confidence]!.color} variant="light">
                              {CONF[l.confidence]!.label}
                              {l.match && r.itemId === l.match.itemId ? ` (${l.match.by})` : ''}
                            </Badge>
                            {it && it.packs.length > 0 && (
                              <Select
                                size="xs"
                                w={170}
                                data={[{ value: '', label: it.unit }, ...it.packs.map((p) => ({ value: p.id, label: `${p.name}` }))]}
                                value={r.packId ?? ''}
                                onChange={(v) => set(l.lp, { packId: v || null })}
                                allowDeselect={false}
                              />
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <NumberInput size="xs" min={0.001} decimalScale={3} decimalSeparator="," value={r.quantity} onChange={(v) => set(l.lp, { quantity: Number(v) || 0 })} />
                        </Table.Td>
                        <Table.Td>
                          <MoneyInput size="xs" valueGr={r.unitPriceGr} onChangeGr={(gr) => set(l.lp, { unitPriceGr: gr ?? 0 })} aria-label="Cena za jednostkę magazynową" />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Anuluj
              </Button>
              <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!warehouseId || !rows.some((r) => r.include && r.itemId) || (foreign && !rate)}>
                Przyjmij
              </Button>
            </Group>
          </Stack>
        )}
      </QueryState>
    </Modal>
  );
}

function LinkModal({ invoiceId, onClose, onDone }: { invoiceId: string; onClose: () => void; onDone: (r: InvoiceWarehouseDto) => void }) {
  const q = useQuery({ queryKey: ['receipt-proposal', invoiceId], queryFn: () => api<InvoiceReceiptProposalDto>(`/finance/purchases/${invoiceId}/receipt-proposal`) });
  const [sel, setSel] = useState<Record<string, number | null>>({});
  const m = useMutation({
    mutationFn: () => {
      const links = Object.entries(sel)
        .filter(([, lp]) => lp !== null)
        .map(([receiptLineId, lp]) => {
          const line = q.data!.receipts.flatMap((r) => r.lines).find((x) => x.lineId === receiptLineId)!;
          return { lp, receiptLineId, quantity: Math.round((line.quantity - line.linked) * 1000) / 1000 };
        });
      return api<InvoiceWarehouseDto>(`/finance/purchases/${invoiceId}/links`, { method: 'POST', body: { links } });
    },
    onSuccess: onDone,
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Powiązanie faktury z przyjęciem" size="lg">
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <Stack>
            <Text size="sm">Towar przyszedł przed fakturą? Wskaż, której pozycji faktury dotyczy pozycja przyjęcia. Stan magazynu się nie zmieni.</Text>
            {q.data.receipts.length === 0 && <Text c="dimmed">Brak przyjęć od tego dostawcy z ostatnich 120 dni.</Text>}
            {q.data.receipts.map((r) => (
              <SectionCard key={r.documentId} title={`${r.number} · ${datePl(r.docDate)}`} padding="sm">
                {r.lines.map((l) => (
                  <Group key={l.lineId} justify="space-between" wrap="nowrap">
                    <Text size="sm">
                      {l.name} — {qtyPl(l.quantity)}
                      {l.linked ? ` (powiązano ${qtyPl(l.linked)})` : ''}
                    </Text>
                    <Select
                      size="xs"
                      w={220}
                      placeholder="pozycja faktury"
                      clearable
                      disabled={l.linked >= l.quantity}
                      data={q.data!.lines.map((x) => ({ value: String(x.lp), label: `${x.lp}. ${x.name}` }))}
                      value={sel[l.lineId] ? String(sel[l.lineId]) : null}
                      onChange={(v) => setSel({ ...sel, [l.lineId]: v ? Number(v) : null })}
                    />
                  </Group>
                ))}
              </SectionCard>
            ))}
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Anuluj
              </Button>
              <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!Object.values(sel).some((v) => v !== null)}>
                Powiąż
              </Button>
            </Group>
          </Stack>
        )}
      </QueryState>
    </Modal>
  );
}
